//TO DO DELETE WHEN THIRD PARTY IS DONE

import { useEffect, useMemo, useState } from "react";
import {
	api,
	ConditionalWrapper,
	ThemeWrapper,
	ThirdPartyPlatform,
	VerityApi,
	useMobileLayout,
	OldThirdPartyStep,
	THIRD_PARTY_IMAGE_MAP,
	EditQuizStep,
	getEditQuizStepName,
} from "@verity/components";
import { Box, Skeleton } from "@mui/material";
import {
	CoreButton,
	CoreModal,
	CorePaper,
	CoreStepper,
	CoreTitleCard,
	coreToast,
	StepConfig,
} from "@verity/components/core";
import { Lms, AssessmentInfo } from "@verity/api-client/types";
import i18n from "../../../i18n";
import EditAssessmentOptionsModal from "./editAssessmentOptionPopup";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import apiClient from "../../../helpers/api-client";
import styles from "./createThirdPartyAssessment.module.css";
import { Platform } from "@verity/url-matcher";

const PlatformCard = (props: {
	id: number;
	platformImage: React.ReactNode;
	platformName: string;
	activePlatformId: number | undefined;
	setActivePlatformId: (platformId: number | undefined) => void;
}) => {
	const { id, platformName, activePlatformId, setActivePlatformId } = props;
	const handleClickEvent = () => setActivePlatformId(id);
	const isMobileLayout = useMobileLayout();

	return (
		<div
			className={`${styles.platformCard} ${isMobileLayout ? styles.platformCardCompact : ""} ${activePlatformId === id ? styles.platformCardActive : ""}`}
			onClick={(e) => {
				handleClickEvent();
				e.preventDefault();
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					handleClickEvent();
				}
			}}
			tabIndex={0}
		>
			<div style={{ height: "67px", display: "flex", alignItems: "center", justifyContent: "center" }}>
				{props.platformImage}
			</div>
			<p
				style={{
					fontSize: "18px",
					fontWeight: "600",
					textAlign: "center",
					color: "rgba(0, 0, 0, 1)",
					userSelect: "none",
				}}
			>
				{platformName}
			</p>
		</div>
	);
};

/**
 * Wraps and Returns the Third Party Assessment form in a Modal
 * @see {@link ThirdPartyAssessment}
 */

export default function CreateThirdPartyQuiz(props: {
	onClose: () => void;
	onFinish: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
}) {
	/**
	 * State Variables
	 */
	const [name, setName] = useState("");
	const [url, setUrl] = useState("");
	const [password, setPassword] = useState<string>();
	const [thirdPartySaveEnabled, setThirdPartySaveEnabled] = useState(true);
	const [savedAssessmentInfo, setSavedAssessmentInfo] = useState<AssessmentInfo | undefined>();
	const [shouldShowThirdPartyAssessmentInfo, setShouldShowThirdPartyAssessmentInfo] = useState(false);
	const [thirdPartyPlatforms, setThirdPartyPlatforms] = useState<ThirdPartyPlatform[]>([]);
	const [activePlatformId, setActivePlatformId] = useState<number | undefined>();
	const [isLoading, setIsLoading] = useState(true);
	const isMobileLayout = useMobileLayout();

	const fixedPageNames: EditQuizStep[] = useMemo(
		() => ["thirdParty", "method", "profile", "accommodations", "confirmation"],
		[],
	);

	const stepConfig = useMemo(() => {
		const config: StepConfig<EditQuizStep>[] = [];

		fixedPageNames.forEach((pageName) => {
			config.push({
				key: pageName,
				label: getEditQuizStepName(pageName),
			});
		});

		return config;
	}, [fixedPageNames]);

	useEffect(() => {
		const fetchPlatformsData = async () => {
			return await apiClient.institute.getInstituteThirdPartyPlatforms();
		};
		fetchPlatformsData()
			.then((platforms) => {
				setThirdPartyPlatforms(Object.values(platforms.selectedPlatformRoster));
				setIsLoading(false);
			})
			.catch((err) => console.error(err));
	}, []);

	useEffect(() => {
		if (!isLoading) {
			setShouldShowThirdPartyAssessmentInfo(Array.from(thirdPartyPlatforms?.values() || []).length === 0);
		}
	}, [thirdPartyPlatforms]);

	/**
	 * Saves the quiz using default configuration options
	 * Reloads the assessments table of the parent
	 * Resets state variables to allow for next creation
	 */

	const saveQuiz = async () => {
		setThirdPartySaveEnabled(false);
		const d2lContentMatch = url.match(/\/d2l\/le\/content\/(\d+)\/viewContent\/(\d+)\/View/);
		let currentQuizId: string = "";

		if (d2lContentMatch) {
			currentQuizId = `d2lContentQuiz_${d2lContentMatch[2]}`;
		} else {
			currentQuizId = crypto.randomUUID().replaceAll("-", "");
		}

		const currentUrl = new URL(window.location.href);
		const urlSearchparam = currentUrl.searchParams;
		const instituteId = parseInt(urlSearchparam.get("instituteId") ?? "");
		const defaultProfile = await api.getDefaultProctoringProfile();
		const institute = await api.getInstitute(instituteId);
		const hostname = new URL(institute.origin ?? "").hostname;
		const quizInfo: AssessmentInfo = {
			lms: urlSearchparam.get("lms") as Lms,
			hostname: hostname,
			courseId: urlSearchparam.get("lmsCourseId") ?? undefined,
			quizId: currentQuizId,
			quizInfo: {
				name: "Third Party: " + name,
			},
		};
		if (!quizInfo) {
			console.error("cannot find quizInfo");
		}
		const loadingToastId = coreToast.loading(
			i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.creatingTP"),
			{ containerId: PORTAL_TOAST_CONTAINER_ID },
		);
		try {
			await api.saveProctoringAssessmentConfiguration({
				assessmentInfo: quizInfo,
				proctoringAssessmentConfiguration: {
					enabled: true,
					thirdPartyAssessmentOption: {
						thirdPartyUrl: encodeURI(url),
						password: password ? password : undefined,
						thirdPartyPlatformId: activePlatformId && activePlatformId > 0 ? activePlatformId : undefined,
					},
				},
				proctoringOption: defaultProfile?.proctoringOption ?? { isExtension: true },
				profileId: defaultProfile?.id,
			});
			coreToast.update(
				loadingToastId,
				i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.addSuccess"),
				"success",
				{
					containerId: PORTAL_TOAST_CONTAINER_ID,
					autoClose: 2500,
				},
			);
			setSavedAssessmentInfo(quizInfo);
		} catch {
			coreToast.update(
				loadingToastId,
				i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.createFail"),
				"error",
				{
					containerId: PORTAL_TOAST_CONTAINER_ID,
				},
			);
		}
		setName("");
		setUrl("");
		setThirdPartySaveEnabled(true);
	};

	const stepper = (
		<CorePaper component="section" className={styles.stepperContainer} sx={{ paddingTop: "15px", marginTop: "10px" }}>
			<div className={styles.stepperHeader}>
				{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.stepperHeader")}
			</div>
			<CoreStepper
				style={{ minWidth: "max-content", paddingTop: "6px", paddingBottom: "6px" }}
				activeStep={0}
				steps={stepConfig}
				noWrapLabel
				variant="scrollable"
			/>
		</CorePaper>
	);

	return (
		<ThemeWrapper>
			<div
				tabIndex={2}
				style={{
					background: isMobileLayout ? "#f4f4f4" : undefined,
					paddingBottom: isMobileLayout && !savedAssessmentInfo ? "50px" : undefined,
				}}
			>
				{!savedAssessmentInfo && (
					<CoreModal
						title={
							isMobileLayout ? undefined : i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.addTP")
						}
						open
						onClose={props.onClose}
						padding={isMobileLayout ? undefined : "30px 20px 20px 20px"}
						minWidth={isMobileLayout ? undefined : "min(75%, 732px)"}
						minHeight={
							isMobileLayout ? undefined : !shouldShowThirdPartyAssessmentInfo ? "min(90%, 650px)" : "min(90%, 455px)"
						}
						widthFixed
						heightFixed
						fullScreen={isMobileLayout}
						showTopCloseButton={!isMobileLayout}
						dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
						buttons={
							!shouldShowThirdPartyAssessmentInfo ? (
								<div className={styles.modalButtons}>
									<div style={{ marginLeft: "auto" }}>
										<CoreButton style={{ marginRight: "10px" }} color="secondary" onClick={props.onClose}>
											{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.cancel")}
										</CoreButton>
										<CoreButton
											color="primary"
											style={{ marginRight: "5px" }}
											onClick={(e) => {
												setShouldShowThirdPartyAssessmentInfo(true);
												e.preventDefault();
											}}
											disabled={activePlatformId === undefined}
										>
											{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.continue")}
										</CoreButton>
									</div>
								</div>
							) : (
								<div className={styles.modalButtons} style={{ justifyContent: "space-between" }}>
									<CoreButton style={{ marginLeft: "5px" }} color="secondary" onClick={props.onClose}>
										{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.cancel")}
									</CoreButton>
									<div>
										<CoreButton
											color="primary"
											style={{ marginRight: "10px" }}
											disabled={Array.from(thirdPartyPlatforms?.values() || []).length === 0}
											onClick={(e) => {
												setShouldShowThirdPartyAssessmentInfo(false);
												e.preventDefault();
											}}
										>
											{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.back")}
										</CoreButton>
										<CoreButton
											color="primary"
											style={{ marginRight: "5px" }}
											onClick={() => {
												return;
											}}
											disabled={!thirdPartySaveEnabled || isLoading}
											form="createThirdPartyAssessmentForm"
											type="submit"
										>
											{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.create")}
										</CoreButton>
									</div>
								</div>
							)
						}
					>
						{isLoading ? (
							<>
								<Skeleton variant="text" height={35} style={{ marginBottom: "20px" }}></Skeleton>
								<div
									className={`${styles.platformCardsWrapper} ${isMobileLayout ? styles.platformCardsWrapperCompact : ""}`}
								>
									{Array.from({ length: 4 }).map((_, index) => (
										<Skeleton
											key={index}
											variant="rectangular"
											width={isMobileLayout ? 160 : 150}
											height={170}
											style={{ borderRadius: "10px" }}
										/>
									))}
								</div>
							</>
						) : !shouldShowThirdPartyAssessmentInfo ? (
							<div
								className={`${styles.platformCardsWrapper} ${isMobileLayout ? styles.platformCardsWrapperCompact : ""}`}
							>
								{isMobileLayout ? (
									<CoreTitleCard
										title={i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.addTP")}
										description={i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.selectTool")}
										style={{
											minWidth: "330px",
											maxWidth: "330px",
											padding: "15px 10px 10px 10px",
											marginTop: "15px",
											boxSizing: "border-box",
										}}
										slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
									/>
								) : (
									<p className={styles.platformCardsWrapperPrompt}>
										{i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.selectTool")}
									</p>
								)}
								<ConditionalWrapper
									condition={isMobileLayout}
									whenTrue={(children) => <div className={styles.platformCards}>{children}</div>}
								>
									{thirdPartyPlatforms
										? Array.from(thirdPartyPlatforms.values()).map((thirdPartyPlatform) => {
												const platformKey = thirdPartyPlatform.platformId as Platform;
												if (platformKey in THIRD_PARTY_IMAGE_MAP) {
													return (
														<PlatformCard
															key={thirdPartyPlatform.platformId}
															id={thirdPartyPlatform.platformId}
															platformImage={THIRD_PARTY_IMAGE_MAP[platformKey]}
															platformName={thirdPartyPlatform.platformName}
															activePlatformId={activePlatformId}
															setActivePlatformId={setActivePlatformId}
														/>
													);
												}
												return null;
											})
										: null}
									<PlatformCard
										id={-1}
										platformImage={THIRD_PARTY_IMAGE_MAP[Platform.DEFAULT]}
										platformName={i18n.t("portal.components.assessmentPage.createThirdPartyQuizTSX.others")}
										activePlatformId={activePlatformId}
										setActivePlatformId={setActivePlatformId}
									/>
								</ConditionalWrapper>
							</div>
						) : (
							<ConditionalWrapper
								condition={isMobileLayout}
								whenTrue={(children) => <div className={styles.navigationContainer}>{children}</div>}
							>
								{isMobileLayout && stepper}
								<Box>
									<form
										style={{ minWidth: isMobileLayout ? undefined : "280px" }}
										onSubmit={async (event) => {
											event.preventDefault();
											await saveQuiz();
										}}
										id="createThirdPartyAssessmentForm"
									>
										{/**
										 * Container for the Third Party Assessment form
										 */}
										<div
											style={{
												display: "flex",
												boxSizing: "border-box",
												width: isMobileLayout ? undefined : "660px",
											}}
										>
											<OldThirdPartyStep
												name={name}
												setName={setName}
												isThirdParty={true}
												thirdPartyUrl={url}
												setThirdPartyUrl={setUrl}
												thirdPartyPassword={password}
												setThirdPartyPassword={setPassword}
												setThirdPartySaveEnabled={setThirdPartySaveEnabled}
											/>
										</div>
									</form>
								</Box>
							</ConditionalWrapper>
						)}
					</CoreModal>
				)}
				{savedAssessmentInfo && (
					<EditAssessmentOptionsModal
						onClose={props.onClose}
						assessment={savedAssessmentInfo}
						isFirstTimeThirdPartyCreation
						afterSave={(options) => {
							props.onFinish(options);
							props.onClose();
						}}
						quizType="lms"
					/>
				)}
			</div>
		</ThemeWrapper>
	);
}
